import { isEmpty } from 'lodash';
import Stripe from 'stripe';

import { PAYMENT_COLLECTION_METHODS } from '@/lib/constants';
import { SubscriptionPlan, SubscriptionPrice, UserSubscriptionInterface } from '@/types';
import { SimpleInvoiceApiInterface } from '@/types/api-types';
import { formatPrice } from '@/utils/format';

import { getUserIsOnPaidTier } from '../permissions/helpers';

// Gets the default plans from a list
export const getDefaultSubscriptionPlan = (plans: SubscriptionPlan[]) => {
  return plans.find((plan) => plan.metadata?.isDefault);
};

// Find a plan by its ID
export const findPlanById = (planId: string, plans: SubscriptionPlan[]) => {
  return plans.find((plan) => plan.id === planId);
};

// Gets the Plan Default Price
export const getPlanPrice = (plan?: SubscriptionPlan | null, getYearPrice?: boolean): SubscriptionPrice | null => {
  if (getYearPrice) {
    // If year price is requested, get that if it exists
    const yearPrice = plan?.planPrices?.find((price) => price.interval === 'year');
    if (yearPrice) return yearPrice;
  }
  // Else just use default (monthly) price
  const defaultPrice = plan?.planPrices?.find((price) => price.interval === 'month');
  return defaultPrice || null;
};

// Get plan price as string
export const getPlanPriceString = (price: SubscriptionPrice, getYearPrice?: boolean): string => {
  const isFreePlan = price.unitAmount === 0;
  if (isFreePlan) return 'Free';

  const unitAmount = getYearPrice && price.unitAmount ? price.unitAmount / 12 : price.unitAmount || 0;
  return `${formatPrice(unitAmount, price.currency || 'usd')}`;
};

export const getPlanIntervalString = (
  price: SubscriptionPrice,
  getYearPrice?: boolean,
  collectionMethod?: Stripe.Subscription.CollectionMethod,
): string => {
  const isFreePlan = price.unitAmount === 0;
  const billing = collectionMethod === PAYMENT_COLLECTION_METHODS.SEND_INVOICE ? 'Invoiced' : 'Billed';

  if (isFreePlan) return 'Try us out before you decide';

  const { unitAmount = 0, currency = 'usd' } = price;
  if (getYearPrice) {
    return `${billing} annually, at ${formatPrice(unitAmount, currency)}/year`;
  }

  return `${billing} monthly`;
};

export const checkIsFreePlan = (plan: SubscriptionPlan): boolean => {
  const monthlyPrice = getPlanPrice(plan);
  return monthlyPrice?.unitAmount === 0;
};

export const checkIsEnterprisePlan = (plan: SubscriptionPlan): boolean => {
  return plan.name === 'Enterprise';
};

export const checkIsPrimaryPlan = (plan: SubscriptionPlan): boolean => {
  const { isPrimary } = plan.metadata || {};
  return isPrimary === 'true';
};

export const filterPrimaryPlans = (plans: SubscriptionPlan[]): SubscriptionPlan[] => {
  if (isEmpty(plans)) return plans;
  return plans.filter((plan) => checkIsPrimaryPlan(plan));
};

export const getSubscribeButtonText = (
  hasSession: boolean,
  subscription: UserSubscriptionInterface | undefined,
  planIsSelected: boolean,
  isFreePlan: boolean,
): string => {
  if (planIsSelected) {
    return 'Your current plan';
  }

  if (hasSession) {
    const userIsOnPaidPlan = subscription ? getUserIsOnPaidTier(subscription) : false;
    if (userIsOnPaidPlan) {
      return 'Manage plan';
    }
    return 'Upgrade now';
  }

  return isFreePlan ? 'Start your trial' : 'Sign up now';
};

/**
 * Converts massive stripe invoice into a simple one
 * @param invoices - Stripe Invoices array
 * @returns Simple Invoice
 */
export const transformStripeInvoices = (invoices: Stripe.Invoice[]): SimpleInvoiceApiInterface[] => {
  return invoices.map((invoice) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { id, total, currency, invoice_pdf, status, lines, created } = invoice;
    const lineDescription = lines.data[0]?.description || '';

    return {
      id,
      total,
      currency,
      invoicePdf: invoice_pdf,
      invoiceDate: new Date(created * 1000).toISOString(),
      status,
      description: lineDescription,
    } as SimpleInvoiceApiInterface;
  });
};
